import { DateTime, Duration } from 'luxon';

// Timezone constants
export const KYIV_TIMEZONE = 'Europe/Kyiv';
export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const DISPLAY_DATE_FORMAT = 'dd.MM.yyyy';
export const ISO_FORMAT = 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ';

/**
 * Creates a DateTime object in the Kyiv timezone from various input types
 */
export function parseToKyiv(date: number | string | Date | DateTime | null | undefined, format?: string): DateTime | null {
  if (!date) return null;
  
  if (date instanceof DateTime) {
    return date.setZone(KYIV_TIMEZONE);
  }
  
  if (date instanceof Date) {
    return DateTime.fromJSDate(date).setZone(KYIV_TIMEZONE);
  }

  if (typeof date === 'number') {
      return DateTime.fromMillis(date).setZone(KYIV_TIMEZONE);
  }
  
  if (format) {
    const parsed = DateTime.fromFormat(date, format, { zone: KYIV_TIMEZONE });
    return parsed.isValid ? parsed : null;
  }
  
  // Try ISO first
  let parsed = DateTime.fromISO(date, { zone: KYIV_TIMEZONE });
  
  // If that fails, try common formats
  if (!parsed.isValid) {
    const formats = ['yyyy-MM-dd', 'dd.MM.yyyy', 'MM/dd/yyyy'];
    
    for (const fmt of formats) {
      parsed = DateTime.fromFormat(date, fmt, { zone: KYIV_TIMEZONE });
      if (parsed.isValid) break;
    }
  }
  
  return parsed.isValid ? parsed : null;
}

/**
 * Returns the current date-time in the Kyiv timezone
 */
export function nowInKyiv(): DateTime {
  return DateTime.now().setZone(KYIV_TIMEZONE);
}

/**
 * Formats a date in the Kyiv timezone
 */
export function formatDate(
  date: string | Date | DateTime | null | undefined, 
  format: string = DISPLAY_DATE_FORMAT
): string {
  const kyivDate = parseToKyiv(date);
  return kyivDate?.toFormat(format) || '';
}

/**
 * Returns ISO string in the Kyiv timezone
 */
export function toISOString(date: string | Date | DateTime | null | undefined): string {
  const kyivDate = parseToKyiv(date);
  return kyivDate?.toISO() || '';
}

/**
 * Returns the start of day for a given date in the Kyiv timezone
 */
export function startOfDay(date: string | Date | DateTime | null | undefined): DateTime | null {
  const kyivDate = parseToKyiv(date);
  return kyivDate?.startOf('day') || null;
}

/**
 * Returns the end of day for a given date in the Kyiv timezone
 */
export function endOfDay(date: string | Date | DateTime | null | undefined): DateTime | null {
  const kyivDate = parseToKyiv(date);
  return kyivDate?.endOf('day') || null;
}

/**
 * Calculates the difference between two dates in the specified unit
 */
export function dateDiff(
  date1: string | Date | DateTime | null | undefined,
  date2: string | Date | DateTime | null | undefined,
  unit: 'years' | 'months' | 'days' | 'hours' = 'days'
): number {
  const kyivDate1 = parseToKyiv(date1);
  const kyivDate2 = parseToKyiv(date2);
  
  if (!kyivDate1 || !kyivDate2) return 0;
  
  return kyivDate2.diff(kyivDate1, [unit]).get(unit);
}

/**
 * Adds a duration to a date in the Kyiv timezone
 */
export function addToDate(
  date: string | Date | DateTime | null | undefined,
  amount: number,
  unit: 'years' | 'months' | 'days' | 'hours'
): DateTime | null {
  const kyivDate = parseToKyiv(date);
  if (!kyivDate) return null;
  
  return kyivDate.plus({ [unit]: amount });
}

/**
 * Checks if the first date is before the second date
 */
export function isBefore(
  date1: string | Date | DateTime | null | undefined,
  date2: string | Date | DateTime | null | undefined,
  unit: 'years' | 'months' | 'days' | 'hours' = 'days'
): boolean {
  const kyivDate1 = parseToKyiv(date1);
  const kyivDate2 = parseToKyiv(date2);
  
  if (!kyivDate1 || !kyivDate2) return false;
  
  if (unit === 'days') {
    return kyivDate1.startOf('day') < kyivDate2.startOf('day');
  }
  
  return kyivDate1 < kyivDate2;
}

/**
 * Checks if the first date is after the second date
 */
export function isAfter(
  date1: string | Date | DateTime | null | undefined,
  date2: string | Date | DateTime | null | undefined,
  unit: 'years' | 'months' | 'days' | 'hours' = 'days'
): boolean {
  const kyivDate1 = parseToKyiv(date1);
  const kyivDate2 = parseToKyiv(date2);
  
  if (!kyivDate1 || !kyivDate2) return false;
  
  if (unit === 'days') {
    return kyivDate1.startOf('day') > kyivDate2.startOf('day');
  }
  
  return kyivDate1 > kyivDate2;
}

/**
 * Checks if the first date is the same as the second date
 */
export function isSame(
  date1: string | Date | DateTime | null | undefined,
  date2: string | Date | DateTime | null | undefined,
  unit: 'years' | 'months' | 'days' | 'hours' = 'days'
): boolean {
  const kyivDate1 = parseToKyiv(date1);
  const kyivDate2 = parseToKyiv(date2);
  
  if (!kyivDate1 || !kyivDate2) return false;
  
  if (unit === 'days') {
    // Compare calendar components directly to avoid timezone issues
    return (
      kyivDate1.year === kyivDate2.year &&
      kyivDate1.month === kyivDate2.month &&
      kyivDate1.day === kyivDate2.day
    );
  } else if (unit === 'months') {
    return (
      kyivDate1.year === kyivDate2.year &&
      kyivDate1.month === kyivDate2.month
    );
  } else if (unit === 'years') {
    return kyivDate1.year === kyivDate2.year;
  }
  
  // For any other unit, use the normalized comparison
  return kyivDate1.startOf('day').equals(kyivDate2.startOf('day'));
}

/**
 * Compare dates based on their calendar values, ignoring timezone differences
 * This is useful for comparing dates like birth dates where the specific time doesn't matter
 */
export function areSameCalendarDates(
  date1: string | Date | DateTime | null | undefined,
  date2: string | Date | DateTime | null | undefined
): boolean {
  const datetime1 = parseToKyiv(date1);
  const datetime2 = parseToKyiv(date2);
  
  if (!datetime1 || !datetime2) return false;
  
  return (
    datetime1.year === datetime2.year &&
    datetime1.month === datetime2.month &&
    datetime1.day === datetime2.day
  );
}

/**
 * Gets the year from a date
 */
export function getYear(date: string | Date | DateTime | null | undefined): number | null {
  const kyivDate = parseToKyiv(date);
  return kyivDate ? kyivDate.year : null;
}

/**
 * Calculates a person's age from birth date
 */
export function getAgeFromDate(birthDate: string | Date | DateTime | null | undefined): number | null {
  const parsed = parseToKyiv(birthDate);
  if (!parsed || !parsed.isValid) return null;
  
  return nowInKyiv().diff(parsed, 'years').years;
}

/**
 * Checks if a date is valid in the Kyiv timezone
 */
export function isValidDate(date: string | Date | DateTime | null | undefined, format?: string): boolean {
  if (!date) return false;
  
  if (date instanceof DateTime) {
    return date.isValid;
  }
  
  return parseToKyiv(date, format) !== null;
}

/**
 * Gets the current day of week (0-6, where 0 is Sunday)
 */
export function getDayOfWeek(date: string | Date | DateTime | null | undefined): number | null {
  const kyivDate = parseToKyiv(date);
  return kyivDate ? kyivDate.weekday % 7 : null;
}
